//Built-in libraries

import Card from '@/components/UI/Card';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
//Services
import { useDashboardData } from '../../../hooks/ReactQueryHooks/useDashboardQuery';
//Assets and Miscellaneous
import type { DashboardType } from '../../../types/Analytics';
import { CurrencyFormatDisplay } from '../../UI/NumberFormatDisplay/CurrencyFormatDisplay';
import Table from '../../UI/Table';
import { Widget } from '../constants';
//Custom components
import WidgetLayout from '../WidgetLayout';

interface NeighborhoodImpactWidgetProps {
  dashboard: DashboardType;
}

const NeighborhoodImpactWidget = ({ dashboard }: NeighborhoodImpactWidgetProps) => {
  const [perPage, setPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [totalPage, _setTotalPage] = useState<number>(1);

  const fetchNeighborhoodImpactWidgetData = useDashboardData({
    dashboard,
    widget: Widget.PROJECTS_BY_NEIGHBORHOOD,
  });

  const neighborhoodImpactWidgetData = fetchNeighborhoodImpactWidgetData?.data?.data;

  const nextPageHandler = () => {
    setPage((prevState) => (prevState === totalPage ? totalPage : page + 1));
  };

  const previousPageHandler = () => {
    setPage((prevState) => (prevState > 1 ? page - 1 : 1));
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'neighborhood',
      header: 'NEIGHBORHOOD',
      accessorKey: 'neighborhood',
      cell: (props) => {
        const { getValue } = props;
        const value = getValue() as string;
        if (value.trim() === '') {
          return 'Unknown';
        }
        return value;
      },
    },
    {
      id: 'projectCount',
      header: () => <div className='text-center'>PROJECTS</div>,
      accessorKey: '_count',
      cell: (props) => {
        const { getValue } = props;
        const value = getValue() as number;
        return (
          <div className='text-center'>
            <span>{value}</span>
          </div>
        );
      },
    },
    {
      id: 'totalDevelopmentValue',
      header: () => <div className='text-center'>TOTAL DEVELOPMENT VALUE</div>,
      accessorKey: 'totalDevelopmentCost',
      cell: (props) => {
        const { getValue } = props;
        const value = getValue() as number;
        return (
          <div className='text-center'>
            <CurrencyFormatDisplay value={value} prefix='$' />
          </div>
        );
      },
    },
  ];

  const tablePagination = {
    page: page,
    pageSize: perPage,
    pageCount: totalPage,
    currentPage: page,
    setPageSize: (v: number) => setPerPage(v),
    nextPage: nextPageHandler,
    previousPage: previousPageHandler,
  };

  return (
    <WidgetLayout dashboard={dashboard} widget={Widget.PROJECTS_BY_NEIGHBORHOOD}>
      {neighborhoodImpactWidgetData && (
        <Card title='Neighborhood Impact' error={false}>
          <div className='h-5/6 overflow-x-auto w-full overflow-hidden'>
            <Table cols={tableColumns} tableData={neighborhoodImpactWidgetData} pagination={tablePagination} height='h-5/6' />
          </div>
        </Card>
      )}
    </WidgetLayout>
  );
};

export default NeighborhoodImpactWidget;
